import HomeIcon from "@mui/icons-material/HomeOutlined";
import {
  AppBar,
  Box,
  Breadcrumbs,
  IconButton,
  Link,
  Toolbar,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Link as RouterLink,
  Outlet,
  useLocation,
  useNavigation,
  useRouteLoaderData,
} from "react-router";
import caseSolvLogo from "../../Case Solv_White.svg";
import { CASE_ROUTE_ID, CaseData } from "../../context/CaseContext";
import { useTheme } from "../../context/ThemeContext";
import { usePersona } from "../../context/usePersona";
import { RouterSkeleton } from "../RouterSkeleton";
import { breadcrumbsOf } from "./breadcrumbs";
import { UserAvatar } from "./UserAvatar";

/**
 * How much of the bar's text colour the parts of the breadcrumb take: the
 * pages on the way are quieter than the page that is open, the separators
 * between them quieter still.
 */
const WAY_ALPHA = 0.74;
const SEPARATOR_ALPHA = 0.4;
const RULE_ALPHA = 0.22;

const NavBar = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  // the path of a case only has its id; its name comes from the loaded case
  const caseData = useRouteLoaderData(CASE_ROUTE_ID) as CaseData | undefined;
  const breadcrumbs = breadcrumbsOf(pathname, t, caseData?.c);
  const ink = theme.palette.primary.contrastText;
  const { active: persona } = usePersona();

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{ zIndex: theme.zIndex.drawer + 1 }}
    >
      <Toolbar
        sx={{
          display: "grid",
          // equal columns on both sides keep the logo in the middle of the
          // bar, whatever stands beside it
          gridTemplateColumns: {
            xs: "minmax(0, 1fr) auto",
            md: "minmax(0, 1fr) auto minmax(0, 1fr)",
          },
          gap: 2,
          px: { xs: 2, sm: 2.5 },
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
        >
          <IconButton
            component={RouterLink}
            to="/"
            aria-label={t("appBar.home")}
            color="inherit"
            size="small"
            edge="start"
          >
            <HomeIcon fontSize="small" />
          </IconButton>
          {breadcrumbs.length > 0 && (
            <>
              <Box
                sx={{
                  flex: "none",
                  width: "1px",
                  height: 18,
                  bgcolor: alpha(ink, RULE_ALPHA),
                }}
              />
              <Breadcrumbs
                aria-label={t("appBar.breadcrumb")}
                separator="/"
                sx={{
                  minWidth: 0,
                  typography: "caption",
                  fontWeight: 600,
                  letterSpacing: ".12em",
                  textTransform: "uppercase",
                  color: alpha(ink, WAY_ALPHA),
                  // the breadcrumb keeps to one line: what does not fit, such
                  // as a long case name, is cut short
                  "& .MuiBreadcrumbs-ol": { flexWrap: "nowrap" },
                  "& .MuiBreadcrumbs-li": {
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                  "& .MuiBreadcrumbs-separator": {
                    color: alpha(ink, SEPARATOR_ALPHA),
                  },
                }}
              >
                {breadcrumbs.map(({ label, to }, index) => {
                  const isOpenPage = index === breadcrumbs.length - 1;
                  return (
                    // the open page is a link as well: following it loads the
                    // page anew
                    <Link
                      key={to}
                      component={RouterLink}
                      to={to}
                      underline="hover"
                      aria-current={isOpenPage ? "page" : undefined}
                      sx={{ color: isOpenPage ? ink : "inherit" }}
                    >
                      {label}
                    </Link>
                  );
                })}
              </Breadcrumbs>
              {/* beside the name of the case on each of its pages: why none
                  of their controls responds */}
              {caseData?.readOnly && (
                <Box
                  component="span"
                  title={t("appBar.readOnlyTip")}
                  sx={{
                    flex: "none",
                    px: 1,
                    py: 0.25,
                    borderRadius: 1.25,
                    typography: "caption",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    bgcolor: alpha(ink, RULE_ALPHA),
                  }}
                >
                  {t("appBar.readOnly")}
                </Box>
              )}
            </>
          )}
        </Box>
        <Box
          component="img"
          src={caseSolvLogo}
          alt={t("appBar.title")}
          sx={{ height: 28, display: { xs: "none", md: "block" } }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          {/* the role a user works in, where it is not the law firm's */}
          {persona !== "lawFirm" && (
            <Box
              component="span"
              sx={{
                px: 1.25,
                py: 0.5,
                borderRadius: 3,
                typography: "caption",
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: theme.palette.secondary.main,
                bgcolor: alpha(theme.palette.secondary.main, 0.18),
              }}
            >
              {t(`appBar.persona.${persona}`)}
            </Box>
          )}
          <UserAvatar />
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export const LoggedInPage = () => {
  const navigation = useNavigation();
  const location = useLocation();
  // Only show skeleton for actual page navigations, not for same-page action submissions
  const isNavigatingAway =
    navigation.state === "loading" &&
    navigation.location?.pathname !== location.pathname;
  return (
    <Box>
      <NavBar />
      {isNavigatingAway ? <RouterSkeleton /> : <Outlet />}
    </Box>
  );
};
